import './DatasetExport.css';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonCard } from '@ionic/react';
import clsx from 'clsx';
import { useSubsetExportData } from '../../api/hooks/useSubsetExportData/useSubsetExportData';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { useSelectedRowsContext } from '../../hooks/context/useSelectedRowsContext';
import useRoutes from '../../hooks/useRoutes';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Dataset, GridRow } from '../../types/types';
import { useExportOptions } from './hooks/useExportOptions';
import { useExportSections } from './hooks/useExportSections';
import { useExportDownload } from './hooks/useExportDownload';
import DatasetExportHeader from './components/DatasetExportHeader';
import ExportColumnsPanel from './components/ExportColumnsPanel';
import ExportOptionsPanel from './components/ExportOptionsPanel';
import ExportSectionsSidebar from './components/ExportSectionsSidebar';
import { ExportMode, SubsetExportState } from './types';

interface DatasetExportLocationState {
  exportSelection?: {
    selectedRows?: GridRow[];
    selectedVirtualRows?: number;
    selectAllChecked?: boolean;
    deselectedRowIds?: string[];
  };
  subsetPathname?: string;
  subsetExport?: SubsetExportState;
}

const DatasetExport: FC = () => {
  const [subsetExportDataset, setSubsetExportDataset] = useState<
    Dataset | undefined
  >(undefined);

  const {
    activeFilters,
    activeSorting,
    dataset,
    data,
    datasetData,
    slaveLazyStates,
    slaveDatasets,
    datasetControl,
  } = useDatasetContext();
  const {
    selectAllChecked,
    selectedRows,
    selectedVirtualRows,
    deselectedRowIds,
  } = useSelectedRowsContext();
  const { loadDatasetDefinition } = useSubsetExportData();
  const { datasetId, exportMode } = useParams<{
    datasetId: string;
    exportMode?: ExportMode;
  }>();
  const history = useHistory();
  const location = useLocation<DatasetExportLocationState | undefined>();
  const routes = useRoutes();
  const resolvedExportMode: ExportMode =
    exportMode === 'with-subsets' ? 'with-subsets' : 'main';
  const isWithSubsetsMode = resolvedExportMode === 'with-subsets';
  const customDelimiterRef = useRef<HTMLInputElement>(null);
  const subsetExportState = location.state?.subsetExport;
  const exportDataset = subsetExportState ? subsetExportDataset : dataset;

  const {
    format,
    encoding,
    delimiterMode,
    customDelimiter,
    decimalSeparator,
    jsonValueMode,
    includeColumnNames,
    useDisplayFormatting,
    header,
    footer,
    exportAll,
    isCustomDelimiterUnsupported,
    isEmptyCustomDelimiterRejected,
    checkCustomDelimiterOnSubmit,
    setExportAll,
    setIncludeColumnNames,
    setUseDisplayFormatting,
    handleFormatChange,
    handleEncodingChange,
    handleDelimiterModeChange,
    handleDecimalSeparatorChange,
    handleJsonValueModeChange,
    handleCustomDelimiterChange,
    handleHeaderChange,
    handleFooterChange,
  } = useExportOptions({ isWithSubsetsMode });

  const rows = useMemo<GridRow[]>(() => {
    const subsetRows = subsetExportState?.rows;

    // Local alias narrows optional subset state before falling back to dataset rows.
    if (Array.isArray(subsetRows)) {
      return subsetRows as GridRow[];
    }

    return Array.isArray(data) ? (data as GridRow[]) : [];
  }, [data, subsetExportState?.rows]);

  const {
    sections,
    subsetDatasets,
    loadingDefinitionIds,
    masterSection,
    subsetSections,
    activeSection,
    hasExportableColumns,
    hasInvalidColumnOptions,
    isLoadingDefinition,
    handleToggleSection,
    handleSelectSection,
    handleToggleColumn,
    handleColumnTextOptionChange,
    handleRoundingModeChange,
    handleReorderColumns,
  } = useExportSections({
    exportDataset,
    rows,
    isWithSubsetsMode,
    subsetExportState,
    slaveDatasets,
  });

  useEffect(() => {
    if (!subsetExportState) {
      setSubsetExportDataset(undefined);
      return;
    }

    // Subset export opens configuration immediately and only loads the subset definition.
    void loadDatasetDefinition({
      subsetDatasetId: subsetExportState.subsetId,
      cachedSubsetDataset: subsetExportDataset,
    }).then((loadedDataset) => {
      setSubsetExportDataset(loadedDataset);
    });
  }, [loadDatasetDefinition, subsetExportState, subsetExportDataset]);

  const routeExportSelection = location.state?.exportSelection;
  const rowsSelectedForExport =
    routeExportSelection?.selectedRows ?? (selectedRows as GridRow[]);
  const virtualRowsSelectedForExport =
    routeExportSelection?.selectedVirtualRows ?? selectedVirtualRows;
  const isSelectAllCheckedForExport =
    routeExportSelection?.selectAllChecked ?? selectAllChecked;
  // Subset exports arrive through history state, where the Set had to be an array.
  const deselectedRowIdsForExport = useMemo<Set<string>>(
    () =>
      routeExportSelection?.deselectedRowIds
        ? new Set(routeExportSelection.deselectedRowIds)
        : deselectedRowIds,
    [routeExportSelection?.deselectedRowIds, deselectedRowIds]
  );
  const selectedRowsCount =
    rowsSelectedForExport.length + virtualRowsSelectedForExport;
  const hasSelectedRows = selectedRowsCount > 0;
  const useAllRows = exportAll || !hasSelectedRows;

  const { isExporting, exportProgress, handleDownload, handleCancelDownload } =
    useExportDownload({
      format,
      encoding,
      delimiterMode,
      customDelimiter,
      decimalSeparator,
      jsonValueMode,
      includeColumnNames,
      useDisplayFormatting,
      header,
      footer,
      isWithSubsetsMode,
      exportDataset,
      dataset,
      rows,
      totalCount: datasetData?.totalCount,
      cachedQueryId: datasetData?.cachedQueryId,
      sections,
      masterSection,
      hasExportableColumns,
      subsetExportState,
      activeFilters,
      datasetControl,
      activeSorting,
      slaveDatasets: subsetDatasets,
      slaveLazyStates,
      rowsSelectedForExport,
      virtualRowsSelectedForExport,
      isSelectAllCheckedForExport,
      deselectedRowIdsForExport,
      useAllRows,
    });

  const handleSaveToDisk = useCallback((): void => {
    if (!checkCustomDelimiterOnSubmit()) {
      customDelimiterRef.current?.focus();
      return;
    }

    handleDownload();
  }, [checkCustomDelimiterOnSubmit, handleDownload]);

  const handleBack = useCallback((): void => {
    if (isExporting) {
      handleCancelDownload();
    }
    if (location.state?.subsetPathname) {
      history.push(location.state.subsetPathname);
      return;
    }

    history.push(routes.datasetList(datasetId));
  }, [history, routes, datasetId, location, isExporting, handleCancelDownload]);

  return (
    <div className="page-container dataset-export-page">
      <IonCard>
        <DatasetExportHeader
          isExporting={isExporting}
          isDisabled={
            !hasExportableColumns ||
            hasInvalidColumnOptions ||
            isLoadingDefinition ||
            isExporting ||
            isCustomDelimiterUnsupported
          }
          progress={exportProgress}
          onBack={handleBack}
          onDownload={handleSaveToDisk}
          onCancel={handleCancelDownload}
        />
        <div
          className={clsx(
            'dataset-export-grid',
            !isWithSubsetsMode && 'main-only'
          )}
        >
          {isWithSubsetsMode && (
            <ExportSectionsSidebar
              masterSection={masterSection}
              subsetSections={subsetSections}
              activeSection={activeSection}
              onSelectSection={handleSelectSection}
              onToggleSection={handleToggleSection}
            />
          )}
          <ExportColumnsPanel
            activeSection={activeSection}
            isWithSubsetsMode={isWithSubsetsMode}
            isLoadingActiveSection={
              !!activeSection &&
              activeSection.kind === 'subset' &&
              loadingDefinitionIds.includes(activeSection.id)
            }
            isLoadingSubsetDefinition={
              !!subsetExportState && !subsetExportDataset
            }
            onToggleColumn={handleToggleColumn}
            onColumnTextOptionChange={handleColumnTextOptionChange}
            onRoundingModeChange={handleRoundingModeChange}
            onReorderColumns={handleReorderColumns}
          />
          <ExportOptionsPanel
            isWithSubsetsMode={isWithSubsetsMode}
            hasSelectedRows={hasSelectedRows}
            selectedRowsCount={selectedRowsCount}
            exportAll={exportAll}
            useAllRows={useAllRows}
            format={format}
            encoding={encoding}
            delimiterMode={delimiterMode}
            customDelimiter={customDelimiter}
            decimalSeparator={decimalSeparator}
            jsonValueMode={jsonValueMode}
            includeColumnNames={includeColumnNames}
            useDisplayFormatting={useDisplayFormatting}
            header={header}
            footer={footer}
            isEmptyCustomDelimiterRejected={isEmptyCustomDelimiterRejected}
            customDelimiterRef={customDelimiterRef}
            onFormatChange={handleFormatChange}
            onEncodingChange={handleEncodingChange}
            onDelimiterModeChange={handleDelimiterModeChange}
            onDecimalSeparatorChange={handleDecimalSeparatorChange}
            onJsonValueModeChange={handleJsonValueModeChange}
            onCustomDelimiterChange={handleCustomDelimiterChange}
            onHeaderChange={handleHeaderChange}
            onFooterChange={handleFooterChange}
            setExportAll={setExportAll}
            setIncludeColumnNames={setIncludeColumnNames}
            setUseDisplayFormatting={setUseDisplayFormatting}
          />
        </div>
      </IonCard>
    </div>
  );
};

export default DatasetExport;
